import { isOnline } from '../store.js';
import { getContext, retryTripRefresh, downloadTripForOffline } from '../trip-context.js';
import { getSyncState, onSyncStatusChange, setSyncStatus } from '../utils/network-fetch.js';
import { escapeHtml } from '../utils/dom.js';
import { formatDateIT, formatDateRangeIT } from '../utils/format.js';
import { classifyBasesForTimeline } from '../utils/current-base.js';
import { daysLeftInStay, getTripTiming } from '../utils/trip-timing.js';
import {
  refreshTripGroupPanel,
} from './trip-group-panel.js';
import { trapModalHistory } from '../utils/modal-history.js';
import {
  ROADTRIP_MENU_LABEL,
  ROADTRIP_SECTIONS,
} from '../config/roadtrip-sections.js';
import { BOTTOM_TABS, NAV_ICONS, resolveBottomTab } from '../icons/nav-icons.js';
import { canEdit, canViewExpenses } from '../store.js';
import { bindTripAiChat, renderTripAiChat } from '../components/trip-ai-chat.js';

const MORE_ITEMS = [
  { tab: 'journal', label: 'Diario', icon: '📔', href: (id) => `#/trip/${id}/journal` },
  { tab: 'customize', label: 'Personalizza', icon: '✏️', href: (id) => `#/trip/${id}/customize/stays` },
];

function tabHref(tripId, tabId) {
  if (tabId === 'more') return null;
  return `#/trip/${tripId}/${tabId}`;
}

function enrichTimeline(timeline, timing) {
  if (timeline.some((b) => b.status === 'current')) return timeline;
  if (timing?.phase !== 'upcoming') return timeline;

  const nextIdx = timeline.findIndex((b) => b.status === 'future');
  if (nextIdx < 0) return timeline;

  return timeline.map((b, i) => (i === nextIdx ? { ...b, status: 'next' } : b));
}

function renderStayNode(base, { isLast, todayKey }) {
  const period = formatDateRangeIT(base.dateStart, base.dateEnd);
  const mapsUrl = base.mapsUrl ?? '#';
  const isCurrent = base.status === 'current';
  const isNext = base.status === 'next';
  const isCompact = base.status === 'future' || base.status === 'past';

  if (isCompact) {
    return `
      <div class="stay-node stay-node--${base.status} stay-node--compact">
        <div class="stay-node__rail" aria-hidden="true">
          <span class="stay-node__dot"></span>
          ${isLast ? '' : '<span class="stay-node__line"></span>'}
        </div>
        <a
          class="stay-node__card stay-node__card--compact"
          href="${escapeHtml(mapsUrl)}"
          target="_blank"
          rel="noopener noreferrer"
          title="Apri ${escapeHtml(base.name)} in Maps"
        >
          <span class="stay-node__name">${escapeHtml(base.name)}</span>
          <span class="stay-node__dates">${escapeHtml(period)}</span>
        </a>
      </div>
    `;
  }

  if (isNext) {
    return `
      <div class="stay-node stay-node--next">
        <div class="stay-node__rail" aria-hidden="true">
          <span class="stay-node__dot"></span>
          ${isLast ? '' : '<span class="stay-node__line"></span>'}
        </div>
        <a
          class="stay-node__card stay-node__card--next"
          href="${escapeHtml(mapsUrl)}"
          target="_blank"
          rel="noopener noreferrer"
          title="Apri ${escapeHtml(base.name)} in Maps"
        >
          <span class="stay-node__badge stay-node__badge--next">Prossimo alloggio</span>
          <span class="stay-node__name">${escapeHtml(base.name)}</span>
          ${base.subtitle ? `<span class="stay-node__subtitle">${escapeHtml(base.subtitle)}</span>` : ''}
          <span class="stay-node__dates">${escapeHtml(period)}</span>
        </a>
      </div>
    `;
  }

  const nightsLeft = isCurrent ? daysLeftInStay(base.dateEnd, todayKey) : null;
  let stayHint = '';
  if (isCurrent && nightsLeft != null) {
    if (nightsLeft <= 1) stayHint = 'Ultimo giorno qui';
    else stayHint = `Ancora ${nightsLeft - 1} ${nightsLeft - 1 === 1 ? 'notte' : 'notti'} qui`;
  }

  return `
    <div class="stay-node stay-node--${base.status}">
      <div class="stay-node__rail" aria-hidden="true">
        <span class="stay-node__dot"></span>
        ${isLast ? '' : '<span class="stay-node__line"></span>'}
      </div>
      <a
        class="stay-node__card"
        href="${escapeHtml(mapsUrl)}"
        target="_blank"
        rel="noopener noreferrer"
        title="Apri ${escapeHtml(base.name)} in Maps"
      >
        ${isCurrent ? '<span class="stay-node__badge">Oggi qui</span>' : ''}
        <span class="stay-node__name">${escapeHtml(base.name)}</span>
        ${base.subtitle ? `<span class="stay-node__subtitle">${escapeHtml(base.subtitle)}</span>` : ''}
        <span class="stay-node__dates">${escapeHtml(period)}</span>
        ${stayHint ? `<span class="stay-node__hint">${escapeHtml(stayHint)}</span>` : ''}
        ${isCurrent ? '<span class="stay-node__maps">Apri in Maps →</span>' : ''}
      </a>
    </div>
  `;
}

function renderTimelineStatus(trip) {
  const timing = getTripTiming(trip);
  if (!timing) return '';

  const countdownClass =
    timing.phase === 'ended' ? 'stay-timeline__countdown stay-timeline__countdown--muted' : 'stay-timeline__countdown';

  return `
    <div class="stay-timeline__status">
      <span class="stay-timeline__today">Oggi · ${escapeHtml(timing.todayLabel)}</span>
      <span class="${countdownClass}">${escapeHtml(timing.statusLine)}</span>
    </div>
  `;
}

function renderBases(bases = [], tripId, trip = null) {
  if (!bases.length) {
    return `
      <section class="stay-timeline stay-timeline--empty" aria-label="Alloggi del viaggio">
        <div class="stay-timeline__head">
          <h2 class="stay-timeline__title">Alloggio</h2>
          <a class="stay-timeline__edit" href="#/trip/${tripId}/customize/stays">Aggiungi</a>
        </div>
        ${renderTimelineStatus(trip)}
        <p class="stay-timeline__empty-hint">Nessun alloggio configurato per il tuo gruppo.</p>
      </section>
    `;
  }

  const timing = getTripTiming(trip);
  const timeline = enrichTimeline(classifyBasesForTimeline(bases), timing);
  const todayKey = timing?.todayKey;
  const nodes = timeline
    .map((base, index) => renderStayNode(base, { isLast: index === timeline.length - 1, todayKey }))
    .join('');

  return `
    <section class="stay-timeline" aria-label="Alloggi del viaggio">
      <div class="stay-timeline__head">
        <h2 class="stay-timeline__title">Alloggio</h2>
        <a class="stay-timeline__edit" href="#/trip/${tripId}/customize/stays">Modifica</a>
      </div>
      ${renderTimelineStatus(trip)}
      <div class="stay-timeline__track">${nodes}</div>
    </section>
  `;
}

function renderBottomNav(tripId, activeTab) {
  const bottomTab = resolveBottomTab(activeTab);
  const tabs = BOTTOM_TABS.filter((tab) => tab.id !== 'expenses' || canViewExpenses());

  const tabsHtml = tabs.map((tab) => {
    const isActive = bottomTab === tab.id;
    const href = tab.isSheet ? null : tabHref(tripId, tab.id);
    const tag = tab.isSheet ? 'button' : 'a';
    const attrs = tab.isSheet
      ? `type="button" id="more-sheet-trigger" aria-haspopup="dialog" aria-expanded="false"`
      : `href="${href}"${isActive ? ' aria-current="page"' : ''}`;

    return `
      <${tag}
        class="bottom-nav__item bottom-nav__item--${tab.accent}${isActive ? ' bottom-nav__item--active' : ''}"
        ${attrs}
      >
        <span class="bottom-nav__icon-wrap">${NAV_ICONS[tab.id]}</span>
        <span class="bottom-nav__label">${escapeHtml(tab.label)}</span>
      </${tag}>
    `;
  }).join('');

  return `<nav class="bottom-nav" aria-label="Navigazione viaggio">${tabsHtml}</nav>`;
}

function renderMoreSheet(tripId, activeTab, activeSection = null) {
  const navItems = MORE_ITEMS.map((item) => {
    const isActive = activeTab === item.tab;
    return `
      <li>
        <a
          class="more-sheet__item${isActive ? ' more-sheet__item--active' : ''}"
          href="${item.href(tripId)}"
          data-close-sheet
        >
          <span class="more-sheet__icon" aria-hidden="true">${item.icon}</span>
          <span class="more-sheet__label">${escapeHtml(item.label)}</span>
        </a>
      </li>
    `;
  }).join('');

  const roadtripItems = ROADTRIP_SECTIONS.map((s) => {
    const isActive = activeSection === s.key;
    return `
      <li>
        <a
          class="more-sheet__item more-sheet__item--roadtrip${isActive ? ' more-sheet__item--active' : ''}"
          href="#/trip/${tripId}/sections/${s.key}"
          data-close-sheet
        >
          <span class="more-sheet__icon" aria-hidden="true">${s.icon}</span>
          <span class="more-sheet__label">${escapeHtml(s.label)}</span>
        </a>
      </li>
    `;
  }).join('');

  return `
    <div class="more-sheet" id="more-sheet" hidden>
      <button type="button" class="more-sheet__backdrop" id="more-sheet-backdrop" aria-label="Chiudi menu"></button>
      <div class="more-sheet__panel" role="dialog" aria-label="Altre opzioni">
        <div class="more-sheet__handle" aria-hidden="true"></div>
        <p class="more-sheet__title">Altro</p>
        <p class="more-sheet__subtitle">Navigazione</p>
        <ul class="more-sheet__list">${navItems}</ul>
        <p class="more-sheet__subtitle">${escapeHtml(ROADTRIP_MENU_LABEL)}</p>
        <ul class="more-sheet__list more-sheet__list--roadtrip">${roadtripItems}</ul>
        <p class="more-sheet__subtitle">Persone</p>
        <ul class="more-sheet__list">
          <li>
            <button type="button" class="more-sheet__item" id="open-trip-group-modal">
              <span class="more-sheet__icon" aria-hidden="true">👥</span>
              <span class="more-sheet__label">Gruppo e inviti</span>
            </button>
          </li>
        </ul>
        <div class="more-sheet__footer">
          <a class="more-sheet__item more-sheet__item--footer" href="#/shelf" data-close-sheet>
            <span class="more-sheet__icon" aria-hidden="true">📚</span>
            <span class="more-sheet__label">Tutti i viaggi</span>
          </a>
          <button type="button" class="more-sheet__item more-sheet__item--footer" id="download-offline-btn">
            <span class="more-sheet__icon" aria-hidden="true">⬇️</span>
            <span class="more-sheet__label">Scarica piano offline</span>
          </button>
        </div>
      </div>
    </div>
  `;
}

function renderTripGroupModal() {
  return `
    <div class="trip-overlay-modal" id="trip-group-modal" hidden>
      <button type="button" class="trip-overlay-modal__backdrop" id="trip-group-modal-backdrop" aria-label="Chiudi"></button>
      <div class="trip-overlay-modal__dialog" role="dialog" aria-labelledby="trip-group-modal-title" aria-modal="true">
        <button type="button" class="trip-overlay-modal__close" id="trip-group-modal-close" aria-label="Chiudi">×</button>
        <h2 id="trip-group-modal-title" class="trip-overlay-modal__title">Gruppo e inviti</h2>
        <div id="trip-group-slot" class="trip-overlay-modal__body"></div>
      </div>
    </div>
  `;
}

function renderSyncBanner(ctx) {
  const sync = ctx?.syncStatus ?? getSyncState().status;
  const cachedAt = ctx?.cachedAt ?? getSyncState().cachedAt;

  if (sync === 'offline' || !isOnline()) {
    return '<div class="sync-banner sync-banner--offline"><span aria-hidden="true">📴</span> Solo lettura — sei offline</div>';
  }
  if (sync === 'refreshing') {
    return '<div class="sync-banner sync-banner--refreshing"><span class="sync-banner__spinner" aria-hidden="true"></span> Aggiornamento in corso…</div>';
  }
  if (sync === 'downloading') {
    return '<div class="sync-banner sync-banner--refreshing"><span class="sync-banner__spinner" aria-hidden="true"></span> Salvataggio piano e mappe offline…</div>';
  }
  if (sync === 'stale' && cachedAt) {
    const label = formatDateIT(cachedAt.slice(0, 10));
    return `<div class="sync-banner sync-banner--stale">Dati del ${label} — <button type="button" class="sync-banner__retry" id="sync-retry-btn">Riprova</button></div>`;
  }
  return '';
}

export function renderTripShell({
  trip,
  activeTab,
  tripId,
  mainHtml,
  members = [],
  bases = [],
  activeSection = null,
  syncContext = null,
}) {
  const ctx = syncContext ?? getContext();
  const showStayTimeline = activeTab === 'map';

  return `
    <div class="app-shell app-shell--trip${activeTab === 'days' ? ' app-shell--days-focus' : ''}">
      <div id="trip-sync-banner">${renderSyncBanner(ctx)}</div>
      <header class="app-header app-header--trip">
        <a class="app-header__back" href="#/shelf" aria-label="Torna ai viaggi">
          <svg class="app-header__back-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </a>
        <div class="app-header__center">
          <h1 class="app-header__title">${escapeHtml(trip.title)}</h1>
          <p class="app-header__subtitle">${escapeHtml(trip.country)}</p>
        </div>
      </header>
      ${showStayTimeline ? renderBases(bases, tripId, trip) : ''}
      <main class="app-main app-main--trip" id="trip-main">
        ${mainHtml}
      </main>
      ${renderBottomNav(tripId, activeTab)}
      ${renderMoreSheet(tripId, activeTab, activeSection)}
      ${renderTripGroupModal()}
      ${renderTripAiChat()}
    </div>
  `;
}

let moreSheetDocCloser = null;
let moreSheetDismiss = null;
let moreSheetContainerRef = null;
let tripGroupModalDismiss = null;
let tripGroupModalContainerRef = null;
let tripGroupModalShellCtx = null;

function hideOverlayModal(modal) {
  if (!modal || modal.hidden) return;
  modal.hidden = true;
}

function hideTripGroupModalUI(container) {
  hideOverlayModal(container?.querySelector('#trip-group-modal'));
}

function closeTripGroupModalFromBack() {
  const container = tripGroupModalContainerRef;
  tripGroupModalDismiss = null;
  tripGroupModalContainerRef = null;
  tripGroupModalShellCtx = null;
  hideTripGroupModalUI(container);
}

export function closeTripGroupModal(container) {
  const modal = container?.querySelector('#trip-group-modal');
  const wasOpen = modal && !modal.hidden;
  if (wasOpen && tripGroupModalDismiss) {
    const dismiss = tripGroupModalDismiss;
    tripGroupModalDismiss = null;
    tripGroupModalContainerRef = null;
    tripGroupModalShellCtx = null;
    dismiss();
  }
  hideTripGroupModalUI(container);
}

export function isTripGroupModalOpen(container) {
  const modal = container?.querySelector('#trip-group-modal');
  return Boolean(modal && !modal.hidden);
}

export function openTripGroupModal(container, shellCtx) {
  const modal = container.querySelector('#trip-group-modal');
  if (!modal) return;
  modal.hidden = false;
  tripGroupModalContainerRef = container;
  tripGroupModalShellCtx = shellCtx;
  tripGroupModalDismiss = trapModalHistory(closeTripGroupModalFromBack);
  if (shellCtx?.instanceId) {
    refreshTripGroupPanel(container, shellCtx).catch(() => {});
  }
}

function hideMoreSheetUI(container) {
  if (!container) return;
  const sheet = container.querySelector('#more-sheet');
  const trigger = container.querySelector('#more-sheet-trigger');
  if (!sheet || sheet.hidden) return;
  sheet.hidden = true;
  trigger?.setAttribute('aria-expanded', 'false');
}

function closeMoreSheetFromBack() {
  const container = moreSheetContainerRef;
  moreSheetDismiss = null;
  moreSheetContainerRef = null;
  hideMoreSheetUI(container);
}

function silentCloseMoreSheet(container) {
  hideMoreSheetUI(container);
  if (moreSheetDismiss) {
    moreSheetDismiss.cancel?.();
    moreSheetDismiss = null;
    moreSheetContainerRef = null;
  }
}

function closeMoreSheet(container) {
  const sheet = container?.querySelector('#more-sheet');
  const wasOpen = sheet && !sheet.hidden;
  if (wasOpen && moreSheetDismiss) {
    const dismiss = moreSheetDismiss;
    moreSheetDismiss = null;
    moreSheetContainerRef = null;
    dismiss();
  }
  hideMoreSheetUI(container);
}

function openMoreSheet(container, shellCtx) {
  const sheet = container.querySelector('#more-sheet');
  const trigger = container.querySelector('#more-sheet-trigger');
  if (!sheet) return;
  sheet.hidden = false;
  trigger?.setAttribute('aria-expanded', 'true');
  updateDownloadOfflineButton(container);
  moreSheetContainerRef = container;
  moreSheetDismiss = trapModalHistory(closeMoreSheetFromBack);
}

export function bindTripShell(container, shellCtx) {
  if (moreSheetDocCloser) {
    document.removeEventListener('keydown', moreSheetDocCloser);
    moreSheetDocCloser = null;
  }

  const trigger = container.querySelector('#more-sheet-trigger');
  const backdrop = container.querySelector('#more-sheet-backdrop');

  trigger?.addEventListener('click', () => {
    const sheet = container.querySelector('#more-sheet');
    if (!sheet) return;
    if (sheet.hidden) openMoreSheet(container, shellCtx);
    else closeMoreSheet(container);
  });

  backdrop?.addEventListener('click', () => closeMoreSheet(container));

  container.querySelectorAll('[data-close-sheet]').forEach((el) => {
    el.addEventListener('click', (e) => {
      if (el instanceof HTMLAnchorElement && el.hash) {
        e.preventDefault();
        const target = el.getAttribute('href');
        silentCloseMoreSheet(container);
        location.hash = target.startsWith('#') ? target : `#${target}`;
        return;
      }
      closeMoreSheet(container);
    });
  });

  container.querySelector('#open-trip-group-modal')?.addEventListener('click', () => {
    silentCloseMoreSheet(container);
    openTripGroupModal(container, shellCtx);
  });

  container.querySelector('#trip-group-modal-backdrop')?.addEventListener('click', () => {
    closeTripGroupModal(container);
  });
  container.querySelector('#trip-group-modal-close')?.addEventListener('click', () => {
    closeTripGroupModal(container);
  });

  moreSheetDocCloser = (e) => {
    if (e.key !== 'Escape') return;
    if (isTripGroupModalOpen(container)) {
      closeTripGroupModal(container);
      return;
    }
    closeMoreSheet(container);
  };
  document.addEventListener('keydown', moreSheetDocCloser);

  container.querySelector('#sync-retry-btn')?.addEventListener('click', () => {
    retryTripRefresh();
  });

  container.querySelector('#download-offline-btn')?.addEventListener('click', async () => {
    if (!shellCtx?.tripId) return;
    if (!isOnline()) {
      alert('Serve connessione per scaricare il piano aggiornato.');
      return;
    }

    silentCloseMoreSheet(container);
    const btn = container.querySelector('#download-offline-btn');
    if (btn) btn.disabled = true;
    setSyncStatus('downloading', getContext()?.cachedAt ?? null);
    updateSyncBanner(container);

    try {
      await downloadTripForOffline(shellCtx.tripId);
      showOfflineSavedBanner(container);
    } catch (err) {
      setSyncStatus(isOnline() ? 'stale' : 'offline', getContext()?.cachedAt ?? null);
      updateSyncBanner(container);
      alert(err.message ?? 'Impossibile salvare il piano offline.');
    } finally {
      updateDownloadOfflineButton(container);
    }
  });

  updateDownloadOfflineButton(container);
  onSyncStatusChange(() => updateSyncBanner(container));
  bindTripAiChat(container);
}

export function refreshTripGroupFromContext(container) {
  const ctx = getContext();
  if (!ctx?.instance?.id || !isTripGroupModalOpen(container)) return Promise.resolve();
  const shellCtx = tripGroupModalShellCtx ?? {
    tripId: ctx.tripId,
    instanceId: ctx.instance.id,
    tripTitle: ctx.trip.title,
  };
  return refreshTripGroupPanel(container, shellCtx);
}

function updateSyncBanner(container) {
  const el = container.querySelector('#trip-sync-banner');
  if (!el) return;
  el.innerHTML = renderSyncBanner(getContext());
}

function showOfflineSavedBanner(container) {
  const el = container.querySelector('#trip-sync-banner');
  if (!el) return;
  el.innerHTML =
    '<div class="sync-banner sync-banner--saved"><span aria-hidden="true">✓</span> Piano salvato per uso offline</div>';
  window.setTimeout(() => updateSyncBanner(container), 4000);
}

function updateDownloadOfflineButton(container) {
  const btn = container.querySelector('#download-offline-btn');
  if (!btn) return;
  const online = isOnline();
  btn.disabled = !online;
  btn.title = online ? '' : 'Serve connessione per scaricare il piano aggiornato';
}

export function getTripMainEl(container) {
  return container.querySelector('#trip-main');
}

/** Aggiorna timeline alloggi dopo modifica in Personalizza (senza re-render completo). */
export function refreshTripBases(container, bases, tripId, trip = null) {
  const section = container.querySelector('.stay-timeline, .stay-timeline--empty');
  if (section) section.outerHTML = renderBases(bases, tripId, trip);
}
